export function hasFileExtension(path) {
  return /^.*\.(jpg|jpeg|png|gif|svg|doc|pdf|xml|xlsx)$/i.test(path)
}

export const isNativePath = (path = '') => {
  const relativePathRegExp = new RegExp(
    '^(?:https?:\\/\\/)?((?:www.)?(freedomdebtrelief|(fdr.(dev|stg|sbx|prd).fdrgcp))).com',
    'i'
  )

  return relativePathRegExp.test(path)
}

export const beautifyUrlPath = (path, trailingSlash = true) => {
  let pathToFix = hasFileExtension(path) ? `/${path}` : `/${path}/`

  pathToFix = pathToFix.replace(/\/+/g, '/')

  return trailingSlash ? pathToFix : pathToFix.replace(/^(.+?)\/*?$/, '$1')
}

// Ensures a full URL ends with a trailing slash (preserving any query string or
// hash fragment) to avoid unnecessary redirects.
export const ensureTrailingSlash = (url) => {
  if (!url) {
    return url
  }

  const [, path, suffix] = url.match(/^([^?#]*)(.*)$/)

  if (path.endsWith('/')) {
    return url
  }

  return `${path}/${suffix}`
}

export function fullUrl(path = '') {
  let finalPath = path

  const isNative = isNativePath(path)

  if (isNative) {
    finalPath = `/${path.replace(
      new RegExp(
        '^(?:https?:\\/\\/)?((?:www.)?(freedomdebtrelief|(fdr.(dev|stg|sbx|prd).fdrgcp))).com'
      ),
      ''
    )}/`
  }

  return beautifyUrlPath(finalPath)
}

export const isPathRelative = (path = '') => {
  const relativePathRegExp = new RegExp('^(http|ftp|tel|mailto|//)', 'i')

  return !relativePathRegExp.test(path)
}

export const removeCurrentDomain = (path = '') => {
  const internalPathRegExp = new RegExp(
    '^(?:https?:\\/\\/)?((((?:www\\.)?(freedomdebtrelief|(fdr.(dev|stg|sbx|prd).fdrgcp)))\\.com)|(localhost:\\d+))',
    'i'
  )

  if (internalPathRegExp.test(path)) {
    return `${path.replace(internalPathRegExp, '')}`
  }

  return path
}

export function formatUrl(path = '') {
  const finalPath = removeCurrentDomain(path)

  return isPathRelative(finalPath) ? beautifyUrlPath(finalPath) : path
}

export function addScriptToHead(url, id) {
  let script = document.getElementById(id)
  if (!script) {
    script = document.createElement('script')
    script.setAttribute('id', id)
    script.src = url
    script.async = true
    document.head.appendChild(script)
  }

  return script
}

// Node Utils

export function generateRandomKey() {
  return Math.random().toString(36).substr(2, 9)
}

export const defaultPhoneNumber = '800-910-0065'
export const controlPhone = '833-582-2310'
export const testPhone = '833-582-7700'

export function numberFormat(value, options) {
  const opts = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }
  const val = Intl.NumberFormat('en-US', opts).format(value)

  return val === 'NaN' ? '' : val
}

export function formatDate(date, options = {}) {
  const {
    locale = 'en-US',
    year = 'numeric',
    month = 'long',
    day = 'numeric',
    timeZone = 'America/Los_Angeles',
  } = options

  const formatOptions = { year, month, day, timeZone }

  if (!date) {
    return ''
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return ''
    }

    const isUtcMidnight =
      date.getUTCHours() === 0 &&
      date.getUTCMinutes() === 0 &&
      date.getUTCSeconds() === 0 &&
      date.getUTCMilliseconds() === 0

    if (isUtcMidnight) {
      return date.toLocaleDateString(locale, {
        ...formatOptions,
        timeZone: 'UTC',
      })
    }

    return new Date(`${date}-1200`).toLocaleDateString(locale, formatOptions)
  }

  if (typeof date !== 'string' || !Date.parse(date)) {
    return ''
  }

  const trimmed = date.trim()
  const hasTimezone = /[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)
  const isUtcDateOnly = /T00:00:00(\.000)?Z$/.test(trimmed)
  const parsed = hasTimezone ? new Date(trimmed) : new Date(`${trimmed}-1200`)

  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  return parsed.toLocaleDateString(locale, {
    ...formatOptions,
    timeZone: isUtcDateOnly ? 'UTC' : timeZone,
  })
}

export function getFirstNameFromFullName(name) {
  return name?.split(' ')[0] || ''
}

export function isNumber(value) {
  const numberRegex = /^\d+$/

  return numberRegex.test(value)
}

export function extractYoutubeID(url) {
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/
  const match = url.match(regExp)

  return match && match[7].length === 11 ? match[7] : ''
}

export function insertItem(arr, index = 0, newItem) {
  return [...arr.slice(0, index), newItem, ...arr.slice(index)]
}

export async function findPaymentReferenceIndex(
  paymentsReferenceTableKeys,
  amount
) {
  let paymentReferenceIndex = -1

  for (let i = 0; i < paymentsReferenceTableKeys.length; i++) {
    if (paymentsReferenceTableKeys[i] === amount) {
      paymentReferenceIndex = i
      break
    } else if (
      paymentsReferenceTableKeys[i] < amount &&
      i + 1 < paymentsReferenceTableKeys.length &&
      paymentsReferenceTableKeys[i + 1] > amount
    ) {
      paymentReferenceIndex = i
      break
    }
  }

  return paymentReferenceIndex
}

export function findClosestHeadingToHyperlink(hyperlinkRef) {
  let closestHeading = null
  let closestDistance = Infinity
  const allHeadings = document.querySelectorAll('h1, h2, h3, h4')

  allHeadings.forEach((heading) => {
    if (heading.id && hyperlinkRef.current) {
      const distance = hyperlinkRef.current.offsetTop - heading.offsetTop

      if (distance < closestDistance && distance > 0) {
        closestDistance = distance
        closestHeading = heading
      }
    }
  })

  return closestHeading?.innerText || ''
}

export function getModifiedDate(updatedAt, publishedDate) {
  const updated = new Date(updatedAt.split('T')[0])
  const published = new Date(publishedDate.split('T')[0])

  return updated.getTime() <= published.getTime()
    ? null
    : updated.toLocaleString()
}

export function getModifiedDateISO(updatedAt, publishedDate) {
  const updated = new Date(updatedAt.split('T')[0])
  const published = new Date(publishedDate.split('T')[0])

  return updated.getTime() <= published.getTime() ? null : updated.toISOString()
}

export function toJsonLdDate(dateString) {
  return new Date(dateString).toString()
}

export const buildQueryString = (baseQuery, userKey, cookies) => {
  let leadId = userKey ? encodeURIComponent(userKey) : undefined
  const urlParams = {
    from: 'fdr',
    utm_source: baseQuery['utm_source'],
    leadId,
    visit_id: cookies?.eh_visit_id || null,
  }

  for (let key in urlParams) {
    if (!urlParams[key]) {
      delete urlParams[key]
    }
  }

  const q = new URLSearchParams(urlParams)

  return q.toString()
}

export const getPhoneNumber = (isHome) => {
  return defaultPhoneNumber
}

export function getTimeFormat(
  monthCount,
  showBreakLine = false,
  timeUnits = {}
) {
  monthCount = Number(monthCount)
  function getPlural(number, word) {
    return (number === 1 && word.one) || word.other
  }

  var months =
      Object.keys(timeUnits.months || {}).length > 0
        ? timeUnits.months
        : { one: 'mos', other: 'mos' },
    years =
      Object.keys(timeUnits.years || {}).length > 0
        ? timeUnits.years
        : { one: 'year', other: 'yrs' },
    m = monthCount % 12,
    y = Math.floor(monthCount / 12),
    result = []
  if (m < 1) {
    return `${y} years`
  } else {
    y && result.push(y + ' ' + getPlural(y, years))
    m && result.push(m + ' ' + getPlural(m, months))
  }

  return result.join(showBreakLine ? '\n' : ' ')
}
